// Before anything else: fills in array methods older Safari lacks (T00VAMP99KWR).
import '@/common/polyfills/arrayChangeByCopy';

// Schriften selbst ausliefern statt von fonts.googleapis.com zu laden: der
// Abruf dort überträgt die IP-Adresse des Besuchers an Google, was deutsche
// Gerichte ohne Einwilligung mehrfach als DSGVO-Verstoß gewertet haben.
// Nebeneffekt: kein render-blockierendes Stylesheet und zwei DNS-Auflösungen
// plus TLS-Handshakes weniger.
import '@fontsource-variable/inter';
import '@fontsource-variable/plus-jakarta-sans';

import {createApp} from 'vue';
import App from './App.vue';
import {vuetify} from './plugins/vuetify';
import {clientCore} from './plugins/clientCore';
import {router} from './common/router';
import {i18n} from './common/i18n';
import {createPinia} from 'pinia';
import '@/types/common/extensions';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import {createAlerts} from '@simpleforyou/client-alerts';
import MainLayout from '@/layout/MainLayout.vue';
import UserLayout from '@/layout/UserLayout.vue';

const app = createApp(App);

import {TinyEmitter} from 'tiny-emitter';
import {ErrorApi} from '@/common/api/ErrorApi';
import {useApiClient} from '@/common/api/useApiClient';
import {directives} from '@/common/directives/directives';
import {describeRejection, shouldReportRejection} from '@/common/utils/errorReporting';

const emitter = new TinyEmitter();
app.config.globalProperties.emitter = emitter;

//================= [DOM PURIFY] ======================================

app.use(VueDOMPurifyHTML);

//================= [LAYOUTS] ======================================

app.component('MainLayout', MainLayout);
app.component('UserLayout', UserLayout);

//================= [INITIALIZE APP] ======================================

const pinia = createPinia();

app.config.globalProperties.window = window;

app.use(vuetify);

// T00B8BPCQBCH — muss VOR client-core stehen: dessen Toast-Klassen
// (ErrorDialog & Co.) greifen auf die Registry zu, sobald sie zum ersten Mal
// etwas melden. Ohne installiertes Plugin wirft client-alerts mit einem
// deutlichen Hinweis, statt still nichts anzuzeigen.
// Ohne Labels: die Vorgaben von client-alerts sind bereits deutsch
// ("Abbrechen", "OK", "Es ist ein unerwarteter Fehler aufgetreten.") und
// decken sich mit dem Bestand. Sie hier aus der i18n der App zu ziehen hiesse,
// sich auf Schlüssel zu verlassen, die nicht in jeder App gleich heissen —
// fehlt einer, zeigt vue-i18n den Schlüsselnamen an.
app.use(createAlerts());

app.use(clientCore);
app.use(router);
app.use(i18n);
app.use(pinia);

const apiClient = useApiClient();

// Derives the component path (e.g. "MainLayout → CookbookShow → RecipeItem → CcImage")
// and the immediate component name from a Vue instance. __name is auto-injected
// from the filename by the SFC compiler for <script setup> and is preserved in
// the production build.
const collectComponentChain = (instance: any): {component: string | null; componentChain: string | null} => {
  if (!instance) {
    return {component: null, componentChain: null};
  }
  const chain: string[] = [];
  let current: any = instance;
  while (current) {
    const options = current.$options ?? current.$?.type;
    const name = options?.__name ?? options?.name ?? null;
    if (name) {
      chain.unshift(name);
    }
    current = current.$parent;
  }
  return {
    component: chain[chain.length - 1] ?? null,
    componentChain: chain.length > 0 ? chain.join(' → ') : null,
  };
};

app.config.errorHandler = (err, instance, info) => {
  const {component, componentChain} = collectComponentChain(instance);
  const payload = {
    // @ts-ignore
    message: err.message,
    // @ts-ignore
    stack: err.stack,
    info,
    component,
    componentChain,
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  if (window.location.origin === 'http://localhost:5173') {
    console.error(payload);
    return;
  }

  apiClient.post(ErrorApi.api(), payload).catch(() => {
  });
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  // Drop benign noise (canceled requests, offline network errors) so the error
  // log stays focused on real problems.
  if (!shouldReportRejection(reason)) {
    return;
  }
  const {name, requestMethod, requestUrl, status} = describeRejection(reason);
  const payload = {
    type: 'unhandledrejection',
    name,
    message: reason?.message ?? String(reason),
    stack: reason?.stack ?? null,
    requestMethod,
    requestUrl,
    status,
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  if (window.location.origin === 'http://localhost:5173') {
    console.error(payload);
    return;
  }
  apiClient.post(ErrorApi.api(), payload).catch(() => {
  });
});

addDirectives(app);

app.mount('#app');

function addDirectives(app: any) {
  directives.forEach((directive) => {
    app.directive(directive.name, directive.directive);
  });
}
